import styles from '$components/DataLayout/DataLayoutWorkspaceMembers/DataLayoutWorkspaceMembers.module.css';
import React from 'react';
import { DELETE_CONFIRM } from '@he-novation/config/paths/modals.constants';
import { ApiKeyInfo } from '@he-novation/config/types/api-key.types';
import { DataLayoutDisplayMode } from '@he-novation/config/types/dataLayout.types';
import { AvatarUser } from '@he-novation/design-system/components/avatars/Avatar/AvatarUser';
import { asyncApiKeyDelete } from '@he-novation/front-shared/async/api-key.async';
import { getHighlightObject } from '@he-novation/front-shared/utils/propsToHighlightedSearchNode';
import objectSearchFactory from '@he-novation/utils/objectSearchFactory';
import { useAtomValue, useSetAtom } from 'jotai';
import { pick } from 'lodash/fp';

import { workspaceApiKeysAtom, workspaceNameAtom } from '$atoms/workspace-atoms';
import { DataLayout } from '$components/DataLayout/DataLayout';
import { SelectionAtom } from '$components/DataLayout/DataLayout.types';
import { sorterFunctionFactory } from '$components/DataLayout/sorterFunctions';
import { useDataLayout } from '$hooks/useDataLayout';
import { useModal } from '$hooks/useModal';
import { useTranslate } from '$hooks/useTranslate';

export type DataLayoutApiKeysProps = {
    items: ApiKeyInfo[];
    dataLayout: ReturnType<typeof useDataLayout>;
    search?: string;
};

export function DataLayoutApiKeys({ items, dataLayout, search }: DataLayoutApiKeysProps) {
    const { t } = useTranslate();
    const { openModal, closeModal } = useModal();

    const workspaceName = useAtomValue(workspaceNameAtom);
    const setApiKeys = useSetAtom(workspaceApiKeysAtom);

    return (
        <DataLayout
            id="data-layout-workspace-api-keys"
            itemToActions={(item: ApiKeyInfo) => [
                {
                    children: t('common.Delete'),
                    icon: 'trash',
                    onClick: () => {
                        openModal(DELETE_CONFIRM, {
                            message: t('common.Are you sure you want to delete this?'),
                            items: [t('settings.API key') + ': ' + item.email],
                            onDelete: async () => {
                                await asyncApiKeyDelete(workspaceName, item.uuid);
                                setApiKeys((prev) =>
                                    prev.filter((apiKey) => apiKey.uuid !== item.uuid)
                                );
                                closeModal();
                            }
                        });
                    }
                }
            ]}
            selectionAtom={undefined}
            itemToId={(item: ApiKeyInfo) => item.uuid}
            items={filterSearch(items, search)}
            extra={{
                search
            }}
            windowed={{
                itemWidth: (_, width) => width,
                itemHeight: (_) => 70,
                itemsPerRow: (width) => Math.floor((width - 20) / 240)
            }}
            columns={[
                {
                    key: 'name',
                    header: t('common.User'),
                    width: 300,
                    sort: sorterFunctionFactory('alphanumeric', 'user.email'),
                    grow: true,
                    render: (item: ApiKeyInfo, _selection: SelectionAtom, { search }) => {
                        const highlighted = highlight(item, search);
                        return (
                            <div className={styles.name}>
                                <AvatarUser
                                    email={item.email}
                                    firstname={item.firstname}
                                    lastname={item.lastname}
                                    picture={item.picture}
                                />
                                <span>
                                    {(highlighted.firstname || highlighted.lastname) && (
                                        <>
                                            <b>
                                                {highlighted.firstname || ''}{' '}
                                                {highlighted.lastname || ''}
                                            </b>
                                            <br />
                                        </>
                                    )}
                                    {highlighted.email}
                                </span>
                            </div>
                        );
                    }
                },
                {
                    key: 'creationDate',
                    header: t('common.Creation date'),
                    width: 200,
                    sort: sorterFunctionFactory('date', 'creationDate'),
                    render: (item: ApiKeyInfo, _selection: SelectionAtom) => {
                        const highlighted = highlight(item, search);
                        return highlighted.creationDate;
                    }
                },
                {
                    key: 'expirationDate',
                    header: t('cast.Expiration date'),
                    width: 200,
                    sort: sorterFunctionFactory('date', 'expirationDate'),
                    render: (item: ApiKeyInfo, _selection: SelectionAtom) => {
                        const highlighted = highlight(item, search);
                        return highlighted.expirationDate;
                    }
                }
            ]}
            displayMode={DataLayoutDisplayMode.List}
            grouper={dataLayout.grouper}
            activeColumns={dataLayout.activeColumns}
            sort={dataLayout.sort}
            onSort={dataLayout.onSort}
            onActiveColumnsChange={dataLayout.onActiveColumnsChange}
        />
    );
}

export const searchProperties = [
    'email',
    'firstname',
    'lastname',
    'creationDate',
    'expirationDate'
];
export const filterSearch = objectSearchFactory(searchProperties);

export const highlight = (item: ApiKeyInfo, search?: string) => {
    const picked = pick(searchProperties)(item);
    return getHighlightObject(
        {
            ...picked,
            creationDate: picked.creationDate?.toLocaleString(),
            expirationDate: picked.expirationDate?.toLocaleString()
        },
        search
    );
};
